<template>
  <div v-if="visible" class="modal-backdrop">
    <div class="modal-content add-row-modal" role="dialog" aria-modal="true">
      <div class="modal-header">
        <h3>
          <i class="fa-solid fa-table-list"></i>
          {{ title }}
        </h3>
        <button
          class="close-btn"
          type="button"
          @click="$emit('close')"
          title="Chiudi"
        >
          &times;
        </button>
      </div>

      <form class="modal-form" @submit.prevent="$emit('submit')">
        <section class="fields-section">
          <div
            v-for="field in baseFields"
            :key="field.index"
            class="form-group"
          >
            <label :for="fieldId(field)">
              {{ field.label }}
              <span v-if="field.required" class="required">*</span>
            </label>
            <select
              v-if="field.type === 'select'"
              :id="fieldId(field)"
              :value="row[field.index] || ''"
              :title="field.title || ''"
              @change="onInput(field, $event.target.value)"
            >
              <option value="" disabled>
                {{ field.placeholder || "Seleziona" }}
              </option>
              <option
                v-for="option in field.options || []"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
            <input
              v-else
              :id="fieldId(field)"
              :value="row[field.index] || ''"
              :placeholder="field.placeholder || ''"
              :title="field.title || ''"
              @input="onInput(field, $event.target.value)"
            />
          </div>
        </section>

        <section v-if="showSoftwareSection" class="software-section">
          <label class="section-label">Sistemi contabili</label>
          <div class="software-grid">
            <button
              v-for="software in softwareOptions"
              :key="software.value"
              type="button"
              class="software-toggle"
              :class="{ selected: isSelected(software.value) }"
              @click="toggleSoftware(software.value)"
            >
              {{ software.label }}
            </button>
          </div>
        </section>

        <section
          v-for="software in selectedSoftwareOptions"
          :key="software.value"
          class="software-fields"
        >
          <h4>{{ software.label }}</h4>
          <div class="fields-grid" :class="fieldsGridClass(software.value)">
            <div
              v-for="field in softwareFields[software.value] || []"
              :key="`${software.value}-${field.index}`"
              class="form-group"
            >
              <label :for="fieldId(field)">
                {{ field.label }}
                <span v-if="field.required" class="required">*</span>
              </label>
              <input
                :id="fieldId(field)"
                :value="row[field.index] || ''"
                :placeholder="field.placeholder || ''"
                :title="field.title || ''"
                :class="{ 'italic-text': row[field.index] === 'N.A.' }"
                @input="onInput(field, $event.target.value)"
              />
            </div>
          </div>
        </section>

        <div class="modal-actions">
          <button
            type="button"
            class="btn-action cancel"
            @click="$emit('close')"
          >
            Annulla
          </button>
          <button type="submit" class="btn-action primary">
            <i class="fa-solid fa-plus"></i>
            Aggiungi riga
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: "AddRowModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      required: true,
    },
    row: {
      type: Array,
      required: true,
    },
    baseFields: {
      type: Array,
      default: () => [],
    },
    softwareOptions: {
      type: Array,
      default: () => [],
    },
    softwareFields: {
      type: Object,
      default: () => ({}),
    },
    selectedSoftwares: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    selectedSoftwareOptions() {
      if (!this.showSoftwareSection) return [];
      return this.softwareOptions.filter((software) =>
        this.selectedSoftwares.includes(software.value)
      );
    },
    showSoftwareSection() {
      return this.softwareOptions.length > 0;
    },
  },
  methods: {
    fieldId(field) {
      return `add-row-field-${field.index}`;
    },
    fieldsGridClass(softwareValue) {
      const count = (this.softwareFields[softwareValue] || []).length;
      return {
        "fields-grid--single": count === 1,
        "fields-grid--three": count === 3,
      };
    },
    isSelected(value) {
      return this.selectedSoftwares.includes(value);
    },
    toggleSoftware(value) {
      const next = this.isSelected(value)
        ? this.selectedSoftwares.filter((item) => item !== value)
        : [...this.selectedSoftwares, value];

      this.$emit("update:selected-softwares", next);
    },
    onInput(field, value) {
      this.$emit("field-input", {
        index: field.index,
        value,
        format: Boolean(field.format),
      });
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  width: min(900px, calc(100vw - 2rem));
  max-width: min(900px, calc(100vw - 2rem));
  max-height: min(760px, calc(100vh - 4.5rem));
  overflow: hidden;
  text-align: left;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #eee;
  background: #f8fafc;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.35rem;
  color: #1e2937;
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
}

.close-btn:hover {
  background: #fee2e2;
  color: #ef4444;
}

.modal-form {
  padding: 1.5rem 2rem 2rem;
  max-height: min(650px, calc(100vh - 10.5rem));
  overflow-y: auto;
}

.fields-section,
.software-section,
.software-fields {
  margin-bottom: 1.35rem;
}

.fields-section {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.fields-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.fields-grid--single {
  grid-template-columns: 1fr;
}

.fields-grid--three .form-group:nth-child(3) {
  grid-column: 2;
}

.form-group label {
  display: flex;
  align-items: flex-end;
  margin-bottom: 0.4rem;
  min-height: 2.35rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.section-label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.95rem;
}

.form-group input,
.form-group select {
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
  color: #1f2937;
  font-size: 1rem;
  transition: all 0.2s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.required {
  color: #ef4444;
}

.software-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
}

.software-toggle {
  min-height: 2.4rem;
  padding: 0.55rem 0.85rem;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  cursor: pointer;
  font-weight: 600;
}

.software-toggle.selected {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.software-fields {
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;
}

.software-fields h4 {
  margin: 0 0 0.85rem;
  font-size: 1rem;
  color: #1f2937;
}

.italic-text {
  font-style: italic;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-top: 1.75rem;
}

.btn-action {
  border: none;
  border-radius: 8px;
  cursor: pointer;
  min-height: 2.6rem;
  padding: 0.75rem 1.25rem;
  font-weight: 700;
}

.btn-action.cancel {
  background: #e5e7eb;
  color: #374151;
}

.btn-action.cancel:hover {
  background: #d1d5db;
}

.btn-action.primary {
  background: #2563eb;
  color: #fff;
}

.btn-action.primary:hover {
  background: #1d4ed8;
}

@media (max-width: 640px) {
  .modal-header,
  .modal-form {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  .modal-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .fields-grid {
    grid-template-columns: 1fr;
  }

  .fields-grid--three .form-group:nth-child(3) {
    grid-column: auto;
  }
}
</style>
